import * as React from 'react';

import { cn } from '@sunfish/lib/utils';

const Card = React.forwardRef<
	HTMLDivElement,
	{
		variant?: 'primary' | 'alternate' | 'divided';
	} & React.HTMLAttributes<HTMLDivElement>
>(({ className, variant = 'divided', ...props }, ref) => (
	<div
		ref={ref}
		className={cn(
			'rounded-xl border shadow flex flex-col',
			{
				'bg-card text-card-foreground': variant === 'primary',
				'bg-card-alternate text-card-alternate-foreground': variant === 'alternate',
				'bg-card text-card-foreground [&>*:nth-child(odd)]:bg-gray-200 [&>*:nth-child(odd)]:text-gray-200-foreground dark:[&>*:nth-child(odd)]:bg-gray-900 dark:[&>*:nth-child(odd)]:text-gray-900-foreground [&>*:first-child]:border-b [&>*:last-child]:border-t [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl':
					variant === 'divided'
			},
			className
		)}
		{...props}
	/>
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('flex flex-col gap-1 p-4 md:p-6', className)} {...props} />
	)
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<h3 ref={ref} className={cn('text-xl font-semibold leading-none tracking-tight', className)} {...props} />
	)
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => (
		<p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
	)
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => <div ref={ref} className={cn('md:p-6 p-4', className)} {...props} />
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => <div ref={ref} className={cn('p-6 flex items-center', className)} {...props} />
);
CardFooter.displayName = 'CardFooter';

const CardInlay = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<div
			className={cn(
				'flex gap-4 -mx-6 p-6 bg-gray-950 bg-opacity-25 border-t-gray-800 border-b-gray-800 border-y',
				className
			)}
			ref={ref}
			{...props}
		/>
	)
);
CardInlay.displayName = 'CardInlay';

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardInlay, CardTitle };
